import React, { useEffect, useState, useContext } from "react";
import styled from "styled-components";
import { UserInfoContext } from "Context/CommonContext";
import { useScheduleBetaContext } from "Pages/MyScheduleBeta/Context/ScheduleBetaContext";
import { getAPIRequest } from "utilFunctions/Axios/AxiosRequestConfiguration";
import { BASE_URL } from "utilFunctions/Constants/urlConstants";
import UnmarkedLectureCount from "Pages/MyScheduleBeta/Components/UnmarkedLectureCount";
import UnmarkedLectureCard from "Pages/MyScheduleBeta/Components/Unmarked/UnmarkedLectureCard";
import { convertTime } from "utilFunctions/helperFunctions/convertTime";
import { BookOutlined, PicLeftOutlined, UserOutlined } from "@ant-design/icons";
import { useMyScheduleBeta } from "Pages/MyScheduleBeta/Hooks/useMyScheduleBeta";
import useResponsive from "Context/useResponsive";
import { Button } from "antd";

const Container = styled.div<{ $isMobile?: boolean }>`
  padding: ${(props) => (props.$isMobile ? "8px" : "12px")};
  display: flex;
  flex-direction: column;
  gap: 16px;
  background-color: #fff;
  box-sizing: border-box;
  width: 100%;
`;
const ScrollableContent = styled.div`
  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: calc(100vh - 170px);
`;

const Header = styled.div<{ $isMobile?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${(props) => (props.$isMobile ? "4px" : "12px")};
  width: 100%;
  box-sizing: border-box;
  flex-wrap: nowrap;
  overflow-x: auto;

  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const Tabs = styled.div<{ $isMobile?: boolean }>`
  display: flex;
  gap: ${(props) => (props.$isMobile ? "4px" : "8px")};
  align-items: center;
  flex-shrink: 1;
  min-width: 0;

  & > * {
    font-size: ${(props) => (props.$isMobile ? "8px" : "12px")} !important;
    padding: ${(props) =>
      props.$isMobile ? "14px 4px" : "4px 12px"} !important;
    white-space: nowrap;
    flex-shrink: 0;
  }
`;

const HeaderActionButton = styled(Button)<{ $isMobile?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  background-color: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-weight: 600;
  color: #334155;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;

  height: ${(props) => (props.$isMobile ? "28px" : "36px")};
  padding: ${(props) => (props.$isMobile ? "0 8px" : "0 12px")};

  .anticon,
  .anticon svg {
    font-size: ${(props) => (props.$isMobile ? "14px" : "16px")} !important;
    width: ${(props) => (props.$isMobile ? "14px" : "16px")};
    height: ${(props) => (props.$isMobile ? "14px" : "16px")};
  }

  &:hover {
    background-color: #f8fafc;
  }
`;

const DateSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const DateHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
`;

const DateText = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: #334155;
  display: flex;
  align-items: center;
  gap: 6px;

  span {
    color: #94a3b8;
    font-weight: 500;
    font-size: 11px;
  }
`;

const UnmarkedPill = styled.div`
  background-color: #f1f5f9;
  color: #64748b;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 16px;
  white-space: nowrap;
`;

const LoadingText = styled.div`
  font-size: 14px;
  color: #64748b;
  text-align: center;
  padding: 40px;
`;

const UnmarkedLectureBody = () => {
  const userInfo = useContext<any>(UserInfoContext);
  const { filterState } = useScheduleBetaContext();
  const { lectureAttendanceUnmarkedCount } = useMyScheduleBeta();
  const { isMobile } = useResponsive();

  const [activeTab, setActiveTab] = useState<"Attendance" | "Syllabus">(
    "Syllabus"
  );
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [sortOrder, setSortOrder] = useState<"desc" | "asc">("desc");

  const fetchNotMarkedData = async (isAttendanceTab: boolean) => {
    const facultyId =
      filterState?.facultySearch?.employeeId ||
      userInfo?.employeeId ||
      "AESLTEST";
    const url = `${BASE_URL}syllabusCompliance/mySchedule/notMarked?facultyId=${facultyId}&isUnmarkedAttendanceStatus=${isAttendanceTab}`;

    await getAPIRequest({
      url,
      success: (response: any) => {
        const responseData = response?.data?.data;
        setData(responseData);
        setLoading(false);
      },
      failure: (error: any) => {
        console.error("Error fetching unmarked data:", error);
        setLoading(false);
      },
    });
  };

  useEffect(() => {
    fetchNotMarkedData(activeTab === "Attendance");
  }, [activeTab, userInfo, filterState?.facultySearch?.employeeId]);

  const totalAttendance = lectureAttendanceUnmarkedCount?.attendance || 0;
  const totalSyllabus = lectureAttendanceUnmarkedCount?.lecture || 0;
  const responseMap = data?.finalResponseMap || {};

  const sortedDates = Object.keys(responseMap).sort((a, b) => {
    return sortOrder === "desc"
      ? new Date(b).getTime() - new Date(a).getTime()
      : new Date(a).getTime() - new Date(b).getTime();
  });

  const getRelativeDateLabel = (dateStr: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const targetDate = new Date(dateStr);
    targetDate.setHours(0, 0, 0, 0);

    const diffTime = today.getTime() - targetDate.getTime();
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    return `${diffDays} days ago`;
  };

  return (
    <Container $isMobile={isMobile}>
      <Header $isMobile={isMobile}>
        <Tabs $isMobile={isMobile}>
          <UnmarkedLectureCount
            label="Lecture"
            count={totalSyllabus}
            icon={<BookOutlined />}
            isActive={activeTab === "Syllabus"}
            onClick={() => setActiveTab("Syllabus")}
          />
          <UnmarkedLectureCount
            label="Attendance"
            count={totalAttendance}
            icon={<UserOutlined />}
            isActive={activeTab === "Attendance"}
            onClick={() => setActiveTab("Attendance")}
          />
        </Tabs>
        <HeaderActionButton
          $isMobile={isMobile}
          onClick={() =>
            setSortOrder((prev) => (prev === "desc" ? "asc" : "desc"))
          }
        >
          <PicLeftOutlined />
        </HeaderActionButton>
      </Header>

      <ScrollableContent>
        {loading ? (
          <LoadingText>Loading unmarked lectures...</LoadingText>
        ) : (
          sortedDates.map((dateStr) => {
            const events = responseMap[dateStr];
            return (
              <DateSection key={dateStr}>
                <DateHeader>
                  <DateText>
                    {convertTime(dateStr, "ddd, DD MMM YYYY")}
                    <span>{getRelativeDateLabel(dateStr)}</span>
                  </DateText>
                  <UnmarkedPill>{events.length} unmarked</UnmarkedPill>
                </DateHeader>
                {events.map((event: any, index: number) => (
                  <UnmarkedLectureCard
                    key={event.fexRecordId || event.batchTimeTableId || index}
                    event={event}
                  />
                ))}
              </DateSection>
            );
          })
        )}
      </ScrollableContent>
    </Container>
  );
};

export default UnmarkedLectureBody;
